<template>
  <div
    class="c-custom-checkbox"
    :class="{
      'c-custom-checkbox_loading': loading,
      'c-custom-checkbox_disabled': isDisabled,
      'c-custom-checkbox_error': isError,
      'c-custom-checkbox_order-icon-first': order === 'icon-first'
    }"
  >
    <input
      :id="id"
      :checked="modelValue"
      type="checkbox"
      class="c-custom-checkbox__input"
      :disabled="loading || isDisabled"
      @change="handleChange"
    />

    <label
      :for="id"
      class="c-custom-checkbox__label"
      :class="{
        'c-custom-checkbox__label_order-icon-first': order === 'icon-first'
      }"
    >
      <span class="c-custom-checkbox__text">
        <slot></slot>
      </span>

      <span
        class="c-custom-checkbox__box"
        :style="{ opacity: isError ? 0.5 : 1 }"
      >
        <svg
          v-if="!loading"
          class="c-custom-checkbox__icon"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
        </svg>
        <CPreloader
          v-else-if="loading"
          size="small"
          no-height
          class="c-custom-checkbox__preloader"
        />
      </span>
    </label>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import CPreloader from '@/components/CPreloader/CPreloader.vue';

  const props = withDefaults(
    defineProps<{
      modelValue: boolean;
      id?: string;
      loading?: boolean;
      order?: 'text-first' | 'icon-first';
      isDisabled?: boolean;
      isError?: boolean;
    }>(),
    {
      order: 'text-first'
    }
  );

  const emit = defineEmits<{
    (e: 'update:modelValue', value: boolean): void;
  }>();

  const id = computed(() => props.id || `checkbox-${Math.random().toString(36).substring(2, 9)}`);

  const handleChange = (event: Event) => {
    const target = event.target as HTMLInputElement;
    emit('update:modelValue', target.checked);
  };
</script>

<style scoped>
  .c-custom-checkbox {
    display: inline-flex;
    position: relative;
  }

  .c-custom-checkbox__input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }

  .c-custom-checkbox__label {
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    user-select: none;
  }

  .c-custom-checkbox_loading .c-custom-checkbox__label {
    cursor: wait;
  }

  .c-custom-checkbox__box {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border: 2px solid #ccc;
    border-radius: 5px;
    background-color: white;
    margin-left: 10px;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
  }

  .c-custom-checkbox_loading .c-custom-checkbox__box {
    border-color: #b8c2cc;
    background-color: #f8fafc;
  }

  .c-custom-checkbox__icon {
    width: 16px;
    height: 16px;
    fill: white;
    opacity: 0;
    transform: scale(0);
    transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  }

  .c-custom-checkbox__input:checked + .c-custom-checkbox__label .c-custom-checkbox__box {
    background-color: #2563eb;
    border-color: #2563eb;
    transform: scale(1.05);
  }

  .c-custom-checkbox_loading
    .c-custom-checkbox__input:checked
    + .c-custom-checkbox__label
    .c-custom-checkbox__box {
    background-color: #d3e1fd;
    border-color: #90b0f5;
  }

  .c-custom-checkbox__input:checked + .c-custom-checkbox__label .c-custom-checkbox__icon {
    opacity: 1;
    transform: scale(1);
  }

  .c-custom-checkbox__input:focus + .c-custom-checkbox__label .c-custom-checkbox__box {
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.3);
  }

  .c-custom-checkbox__text {
    font-size: 16px;
    font-weight: 500;
  }

  .c-custom-checkbox_loading .c-custom-checkbox__text {
    opacity: 0.7;
  }

  /* Styling for preloader */
  .c-custom-checkbox__preloader {
    transform: scale(0.6);
    position: relative;
    right: -1px;
  }

  .c-custom-checkbox__preloader :deep(.loader) {
    width: 16px !important;
    height: 16px !important;
    border-width: 3px !important;
  }

  .c-custom-checkbox_loading
    .c-custom-checkbox__input:not(:checked)
    + .c-custom-checkbox__label
    .c-custom-checkbox__box {
    background-color: #f8fafc;
  }

  /* Hover effect */
  .c-custom-checkbox__label:hover .c-custom-checkbox__box {
    border-color: #2563eb;
    transform: scale(1.05);
  }

  .c-custom-checkbox_loading .c-custom-checkbox__label:hover .c-custom-checkbox__box {
    border-color: #b8c2cc;
    transform: none;
  }

  .c-custom-checkbox_disabled .c-custom-checkbox__label:hover .c-custom-checkbox__box {
    border-color: #b8c2cc;
    transform: none;
  }

  .c-custom-checkbox__label {
    &_order-icon-first {
      flex-direction: row-reverse;

      .c-custom-checkbox__text {
        margin-right: 10px;
        margin-left: 10px;
      }

      .c-custom-checkbox__box {
        margin-right: 0;
        margin-left: 0;
      }
    }
  }

  /* Animation for check mark */
  @keyframes checkmark {
    0% {
      transform: scale(0) rotate(-45deg);
      opacity: 0;
    }
    100% {
      transform: scale(1) rotate(0);
      opacity: 1;
    }
  }

  .c-custom-checkbox__input:checked + .c-custom-checkbox__label .c-custom-checkbox__icon {
    animation: checkmark 0.3s cubic-bezier(0.65, 0, 0.35, 1) forwards;
  }

  /* Ripple effect on click */
  @keyframes ripple {
    0% {
      transform: scale(0);
      opacity: 0.5;
    }
    100% {
      transform: scale(2);
      opacity: 0;
    }
  }

  .c-custom-checkbox__label:active::after {
    content: '';
    position: absolute;
    width: 24px;
    height: 24px;
    right: 11px;
    top: 11px;
    transform: translate(50%, -50%);
    background-color: rgba(37, 99, 235, 0.2);
    border-radius: 50%;
    animation: ripple 0.5s ease-out;
  }

  /* Error state styles */
  .c-custom-checkbox_error .c-custom-checkbox__box {
    border-color: var(--g-color-error);
  }

  .c-custom-checkbox_error .c-custom-checkbox__label:hover .c-custom-checkbox__box {
    border-color: var(--g-color-error);
  }

  .c-custom-checkbox_error .c-custom-checkbox__text {
    color: var(--g-color-error);
  }

  .c-custom-checkbox_error
    .c-custom-checkbox__input:focus
    + .c-custom-checkbox__label
    .c-custom-checkbox__box {
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.3);
  }

  .c-custom-checkbox_error
    .c-custom-checkbox__input:checked
    + .c-custom-checkbox__label
    .c-custom-checkbox__box {
    background-color: var(--g-color-error);
    border-color: var(--g-color-error);
  }

  .c-custom-checkbox_error
    .c-custom-checkbox__input:disabled
    + .c-custom-checkbox__label
    .c-custom-checkbox__box {
    background-color: rgb(255, 210, 210);
  }
</style>
